import { supabase } from '../../supabase.js';
import { validateAndGetTenantUuid } from '../../utils/tenantUtils.js';
import { ServiceError } from '../../utils/errorHandler.js';

class AppDeveloperService {
  constructor() {
    this.developersTable = 'app_developers';
    this.appsTable = 'apps';
    this.reviewsTable = 'app_reviews';
  }

  // Developer Registration and Management
  async registerDeveloper(developerData, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.developersTable)
        .insert({
          ...developerData,
          tenant_id: tenantUuid,
          verification_status: 'pending'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ServiceError('DEVELOPER_REGISTRATION_FAILED', error.message);
    }
  }

  async getDeveloperById(developerId, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.developersTable)
        .select('*')
        .eq('id', developerId)
        .eq('tenant_id', tenantUuid)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ServiceError('DEVELOPER_NOT_FOUND', error.message);
    }
  }

  async getDeveloperByEmail(email, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.developersTable)
        .select('*')
        .eq('developer_email', email)
        .eq('tenant_id', tenantUuid)
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ServiceError('DEVELOPER_NOT_FOUND', error.message);
    }
  }

  async updateDeveloper(developerId, updateData, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.developersTable)
        .update({
          ...updateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', developerId)
        .eq('tenant_id', tenantUuid)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ServiceError('DEVELOPER_UPDATE_FAILED', error.message);
    }
  }

  // App Submission and Management
  async submitApp(appData, developerId, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.appsTable)
        .insert({
          ...appData,
          developer_id: developerId,
          tenant_id: tenantUuid,
          app_status: 'submitted'
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ServiceError('APP_SUBMISSION_FAILED', error.message);
    }
  }

  async getDeveloperApps(developerId, tenant_id, filters = {}) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      let query = supabase
        .from(this.appsTable)
        .select(`
          *,
          app_reviews (
            id,
            review_status,
            overall_score,
            review_date
          )
        `)
        .eq('developer_id', developerId)
        .eq('tenant_id', tenantUuid);

      // Apply filters
      if (filters.status) {
        query = query.eq('app_status', filters.status);
      }
      if (filters.category) {
        query = query.eq('app_category', filters.category);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ServiceError('APPS_FETCH_FAILED', error.message);
    }
  }

  async updateApp(appId, updateData, developerId, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.appsTable)
        .update({
          ...updateData,
          updated_at: new Date().toISOString()
        })
        .eq('id', appId)
        .eq('developer_id', developerId)
        .eq('tenant_id', tenantUuid)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ServiceError('APP_UPDATE_FAILED', error.message);
    }
  }

  async deleteApp(appId, developerId, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { error } = await supabase
        .from(this.appsTable)
        .delete()
        .eq('id', appId)
        .eq('developer_id', developerId)
        .eq('tenant_id', tenantUuid);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      throw new ServiceError('APP_DELETION_FAILED', error.message);
    }
  }

  // Developer Analytics
  async getDeveloperAnalytics(developerId, tenant_id, dateRange = {}) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      // Get apps count
      const { data: apps, error: appsError } = await supabase
        .from(this.appsTable)
        .select('id, app_status, total_downloads, total_revenue, rating')
        .eq('developer_id', developerId)
        .eq('tenant_id', tenantUuid);

      if (appsError) throw appsError;

      // Calculate analytics
      const analytics = {
        totalApps: apps.length,
        activeApps: apps.filter(app => app.app_status === 'active').length,
        pendingApps: apps.filter(app => app.app_status === 'submitted').length,
        totalDownloads: apps.reduce((sum, app) => sum + (app.total_downloads || 0), 0),
        totalRevenue: apps.reduce((sum, app) => sum + (parseFloat(app.total_revenue) || 0), 0),
        averageRating: apps.length > 0 ? 
          apps.reduce((sum, app) => sum + (app.rating || 0), 0) / apps.length : 0
      };

      return analytics;
    } catch (error) {
      throw new ServiceError('ANALYTICS_FETCH_FAILED', error.message);
    }
  }

  // Developer Verification
  async verifyDeveloper(developerId, verificationData, tenant_id) {
    try {
      const tenantUuid = await validateAndGetTenantUuid(tenant_id);
      
      const { data, error } = await supabase
        .from(this.developersTable)
        .update({
          verification_status: verificationData.status,
          api_key: verificationData.status === 'verified' ? 
            `dev_${developerId}_${Date.now()}` : null,
          updated_at: new Date().toISOString()
        })
        .eq('id', developerId)
        .eq('tenant_id', tenantUuid)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (error) {
      throw new ServiceError('DEVELOPER_VERIFICATION_FAILED', error.message);
    }
  }
}

export const appDeveloperService = new AppDeveloperService();
